import { CheckCircle, Clock, MessageSquareText, Search, XCircle } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { refundService, type Refund } from '../../../shared/services/refundService';
import { userService } from '../../../shared/services/userService';
import { useShop } from '../../../shared/store';

type RefundStatusFilter = 'all' | Refund['status'];

const getRefundStatusStyles = (status: Refund['status']): string => {
  if (status === 'approved') return 'bg-emerald-100 text-emerald-700';
  if (status === 'processed') return 'bg-sky-100 text-sky-700';
  if (status === 'rejected') return 'bg-rose-100 text-rose-700';
  return 'bg-amber-100 text-amber-700';
};

const formatRefundDate = (value: string): string => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
};

const AdminRefundPage = () => {
  const { t } = useTranslation();
  const { orders } = useShop();
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRefund, setSelectedRefund] = useState<Refund | null>(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState<RefundStatusFilter>('all');
  const [searchValue, setSearchValue] = useState('');
  const [usersById, setUsersById] = useState<Record<string, string>>({});
  const orderById = useMemo(() => new Map(orders.map((order) => [order.id, order])), [orders]);

  const resolveProductName = useMemo(
    () => (nameKey: string): string => {
      const translated = t(nameKey);
      if (translated !== nameKey) {
        return translated;
      }
      const fallback = nameKey
        .split('.')
        .at(-1)
        ?.replace(/[-_]+/g, ' ')
        .trim();
      return fallback && fallback.length > 0 ? fallback : nameKey;
    },
    [t],
  );

  const resolveOrderTitle = useMemo(
    () =>
      (order?: { orderName?: string; items?: Array<{ product?: { nameKey?: string } }> }): string => {
        const explicitName = typeof order?.orderName === 'string' ? order.orderName.trim() : '';
        if (explicitName) {
          return explicitName;
        }

        const itemNames = (order?.items ?? [])
          .map((item) => (typeof item.product?.nameKey === 'string' ? item.product.nameKey.trim() : ''))
          .filter(Boolean)
          .map((nameKey) => resolveProductName(nameKey));

        if (itemNames.length === 0) {
          return 'Product';
        }

        return itemNames.join(', ');
      },
    [resolveProductName],
  );

  useEffect(() => {
    void loadRefunds();
  }, []);

  const loadRefunds = async () => {
    try {
      setIsLoading(true);
      setError('');
      const [data, users] = await Promise.all([
        refundService.getAllRefunds(),
        userService.getUsers().catch(() => []),
      ]);
      setUsersById(
        users.reduce<Record<string, string>>((acc, user) => {
          acc[user.id] = user.name || user.email || 'Customer';
          return acc;
        }, {}),
      );
      setRefunds(
        [...data].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()),
      );
    } catch {
      setError('Failed to load refunds');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (refundId: string, status: Refund['status']) => {
    try {
      setIsLoading(true);
      setError('');
      setSuccess('');
      await refundService.updateRefundStatus(refundId, status, adminResponse.trim() || undefined);
      setSuccess(`Refund ${status} successfully.`);
      setAdminResponse('');
      setSelectedRefund(null);
      await loadRefunds();
    } catch {
      setError('Failed to update refund');
    } finally {
      setIsLoading(false);
    }
  };

  const stats = useMemo(
    () => ({
      total: refunds.length,
      pending: refunds.filter((refund) => refund.status === 'pending').length,
      approved: refunds.filter((refund) => refund.status === 'approved').length,
      processed: refunds.filter((refund) => refund.status === 'processed').length,
      rejected: refunds.filter((refund) => refund.status === 'rejected').length,
    }),
    [refunds],
  );

  const filteredRefunds = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return refunds.filter((refund) => {
      if (statusFilter !== 'all' && refund.status !== statusFilter) {
        return false;
      }

      if (!normalizedSearch) {
        return true;
      }

      const buyerLabel =
        (
          refund.buyerName ||
          (refund.buyerId ? usersById[refund.buyerId] : undefined) ||
          refund.buyerEmail ||
          ''
        ).toLowerCase();
      const orderTitle = resolveOrderTitle(orderById.get(refund.orderId)).toLowerCase();

      return (
        refund.orderId.toLowerCase().includes(normalizedSearch) ||
        orderTitle.includes(normalizedSearch) ||
        refund.reason.toLowerCase().includes(normalizedSearch) ||
        refund.status.toLowerCase().includes(normalizedSearch) ||
        (refund.adminResponse ?? '').toLowerCase().includes(normalizedSearch) ||
        buyerLabel.includes(normalizedSearch)
      );
    });
  }, [orderById, refunds, resolveOrderTitle, searchValue, statusFilter, usersById]);

  return (
    <div className="space-y-6">
      {error && <div className="rounded-lg bg-rose-50 p-4 text-rose-700">{error}</div>}
      {success && <div className="rounded-lg bg-emerald-50 p-4 text-emerald-700">{success}</div>}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <div className="rounded-2xl border border-orange-100 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">All Requests</p>
          <p className="mt-2 text-3xl font-semibold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Pending</p>
          <p className="mt-2 text-3xl font-semibold text-amber-900">{stats.pending}</p>
        </div>
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Approved</p>
          <p className="mt-2 text-3xl font-semibold text-emerald-900">{stats.approved}</p>
        </div>
        <div className="rounded-2xl border border-sky-100 bg-sky-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Processed</p>
          <p className="mt-2 text-3xl font-semibold text-sky-900">{stats.processed}</p>
        </div>
        <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">Rejected</p>
          <p className="mt-2 text-3xl font-semibold text-rose-900">{stats.rejected}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-orange-100 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">All Refund Requests</h2>
            <p className="mt-1 text-sm text-slate-600">Review every refund request and the complaint submitted by the customer.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="relative min-w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <input
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search product or complaint..."
                className="w-full rounded-xl border border-orange-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 focus:border-orange-400 focus:outline-none"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as RefundStatusFilter)}
              className="rounded-xl border border-orange-200 bg-white px-4 py-2.5 text-sm text-slate-900 focus:border-orange-400 focus:outline-none"
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="processed">Processed</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <p className="mt-6 text-sm text-slate-600">Loading refund requests...</p>
        ) : filteredRefunds.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-orange-200 bg-orange-50/50 p-8 text-center text-sm text-slate-600">
            No refund requests matched your search.
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-orange-100">
              <thead className="bg-orange-50 text-left text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Complaint / Reason</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Terms</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Requested</th>
                  <th className="px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-orange-50 text-sm text-slate-700">
                {filteredRefunds.map((refund, index) => (
                  <tr key={`${refund.id}-${refund.createdAt}-${index}`} className="align-top">
                    <td className="px-4 py-4">
                      <p className="font-semibold text-slate-900">
                        {resolveOrderTitle(orderById.get(refund.orderId))}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">
                        Customer:{' '}
                        {refund.buyerName ||
                          (refund.buyerId ? usersById[refund.buyerId] : undefined) ||
                          refund.buyerEmail ||
                          'Customer'}
                      </p>
                    </td>
                    <td className="px-4 py-4">
                      <div className="rounded-xl border border-orange-100 bg-orange-50/60 p-3">
                        <div className="flex items-start gap-2">
                          <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                          <p className="text-sm leading-6 text-slate-700">{refund.reason}</p>
                        </div>
                        {refund.adminResponse && (
                          <div className="mt-3 rounded-lg bg-white px-3 py-2 text-xs text-slate-600">
                            <span className="font-semibold text-slate-700">Admin response:</span> {refund.adminResponse}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 font-semibold text-slate-900">$ {refund.amount.toFixed(2)}</td>
                    <td className="px-4 py-4">
                      <span className={refund.acceptedTerms ? 'text-emerald-700' : 'text-rose-600'}>
                        {refund.acceptedTerms ? 'Accepted' : 'Not accepted'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getRefundStatusStyles(refund.status)}`}>
                        {refund.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-xs text-slate-500">{formatRefundDate(refund.createdAt)}</td>
                    <td className="px-4 py-4">
                      <div className="flex min-w-[180px] flex-col gap-2">
                        {refund.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedRefund(refund);
                                setAdminResponse(refund.adminResponse ?? '');
                              }}
                              className="rounded-lg bg-emerald-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-600"
                            >
                              Review & Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => void handleUpdateStatus(refund.id, 'rejected')}
                              className="rounded-lg bg-rose-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-rose-600"
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {refund.status === 'approved' && (
                          <button
                            type="button"
                            onClick={() => void handleUpdateStatus(refund.id, 'processed')}
                            className="rounded-lg bg-sky-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-sky-600"
                          >
                            Mark Processed
                          </button>
                        )}
                        {refund.status !== 'pending' && refund.status !== 'approved' && (
                          <span className="text-xs font-medium text-slate-500">No action needed</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedRefund && (
        <div className="rounded-2xl border border-orange-100 bg-white p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">Refund Review</h3>
              <p className="mt-1 text-sm text-slate-600">
                {resolveOrderTitle(orderById.get(selectedRefund.orderId))} - RWF {selectedRefund.amount.toFixed(2)}
              </p>
            </div>
            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getRefundStatusStyles(selectedRefund.status)}`}>
              {selectedRefund.status}
            </span>
          </div>

          <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Customer Complaint</p>
            <p className="mt-2 text-sm leading-6 text-slate-700">{selectedRefund.reason}</p>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-semibold text-slate-700">Admin Response</label>
            <textarea
              value={adminResponse}
              onChange={(event) => setAdminResponse(event.target.value)}
              placeholder="Write your response to the customer..."
              rows={4}
              className="mt-2 w-full rounded-xl border border-orange-200 px-4 py-3 text-sm focus:border-orange-400 focus:outline-none"
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => void handleUpdateStatus(selectedRefund.id, 'approved')}
              disabled={isLoading}
              className="rounded-lg bg-emerald-500 px-4 py-2 font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Approve Refund'}
            </button>
            <button
              type="button"
              onClick={() => {
                setSelectedRefund(null);
                setAdminResponse('');
              }}
              className="rounded-lg border border-slate-300 px-4 py-2 font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-orange-100 bg-white p-5">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600" />
            <p className="font-semibold text-slate-900">Pending</p>
          </div>
          <p className="mt-3 text-sm text-slate-600">{stats.pending} requests waiting for admin review.</p>
        </div>
        <div className="rounded-2xl border border-orange-100 bg-white p-5">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-emerald-600" />
            <p className="font-semibold text-slate-900">Approved</p>
          </div>
          <p className="mt-3 text-sm text-slate-600">{stats.approved} requests have been approved.</p>
        </div>
        <div className="rounded-2xl border border-orange-100 bg-white p-5">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-sky-600" />
            <p className="font-semibold text-slate-900">Processed</p>
          </div>
          <p className="mt-3 text-sm text-slate-600">{stats.processed} requests have been completed.</p>
        </div>
        <div className="rounded-2xl border border-orange-100 bg-white p-5">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-rose-600" />
            <p className="font-semibold text-slate-900">Rejected</p>
          </div>
          <p className="mt-3 text-sm text-slate-600">{stats.rejected} requests were rejected.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminRefundPage;
